import { type BaseItem, type NullablePartial } from './common.js';
import { Payor as DeprecatedPayor } from './patient/insuranceInformation.js';
import type * as Clearinghouse from './payor/clearinghouse.js';
import { type PayorClearinghouse } from './payor/clearinghouse.js';

interface BasePayor {
  name: string;
  predictGroup: string;
  displayName: string;
  deleted: Date | null;
  plans: string[];
  clearinghouses: PayorClearinghouse[];
}

export type PayorClearinghouseUpsertValues = Omit<Clearinghouse.CreateValues, 'payorId'> & {
  id?: string;
};
export interface Payor extends BaseItem, BasePayor {}
export type CreateValues = Omit<NullablePartial<BasePayor>, 'deleted' | 'clearinghouses'> & {
  clearinghouses: PayorClearinghouseUpsertValues[];
};
export type UpdateValues = Partial<CreateValues>;

export type FindAllOptions = Partial<{
  name: string;
  includeDeleted: boolean;
}>;

// goal: replace this with payor table / formatPayorText
export const payorMap: {
  [payor in DeprecatedPayor]: string;
} = {
  [DeprecatedPayor.Aarp]: 'AARP',
  [DeprecatedPayor.AarpGroupHealthcareOptions]: 'AARP Group Healthcare options',
  [DeprecatedPayor.AarpMedicareAdvantageUnitedHealthcare]:
    'AARP - Medicare Advantage United Healthcare',
  [DeprecatedPayor.AarpUnitedHealthcare]: 'AARP - United Healthcare',
  [DeprecatedPayor.Aetna]: 'Aetna',
  [DeprecatedPayor.AetnaBetterHealthKansas]: 'Aetna Better Health Kansas',
  [DeprecatedPayor.AetnaMedicare]: 'Aetna Medicare Advantage',
  [DeprecatedPayor.AetnaSeniorSupplemental]: 'Aetna Senior Supplemental',
  [DeprecatedPayor.AlliedBenefitSystems]: 'Allied Benefit Systems',
  [DeprecatedPayor.AmbetterFromSuperiousHealthPlanCommercial]: 'Ambetter Health Plan (Superior)',
  [DeprecatedPayor.AmericanFamily]: 'American Family',
  [DeprecatedPayor.AmericanMedicalSecurity]: 'American Medical Security',
  [DeprecatedPayor.AmericanRepublic]: 'American Republic',
  [DeprecatedPayor.AmeriHealth]: 'AmeriHealth',
  [DeprecatedPayor.AmeriHealthCaritas]: 'Amerihealth Caritas',
  [DeprecatedPayor.AmeriHealthOfNewJersey]: 'Amerihealth Of New Jersey',
  [DeprecatedPayor.AnthemBlueCrossBlueShield]: 'Anthem - Blue Cross Blue Shield',
  [DeprecatedPayor.ApwuHealthPlan]: 'APWU Health Plan',
  [DeprecatedPayor.AspireMedicare]: 'Aspire Medicare',
  [DeprecatedPayor.AsurisNorthwestHealth]: 'Asuris Northwest Health',
  [DeprecatedPayor.AutomatedBenefitServicesSmartHealth]:
    'Automated Benefit Services (ABS) / SmartHealth',
  [DeprecatedPayor.AutomatedHealthSystems]: 'Automated Health Systems',
  [DeprecatedPayor.BankersLife]: 'Bankers Life',
  [DeprecatedPayor.BcbsBlueCrossAndBlueShield]: 'BCBS Blue Cross And Blue Shield',
  [DeprecatedPayor.BcbsKansas]: 'BCBS Blue Cross And Blue Shield Kansas',
  [DeprecatedPayor.BcbsKansasCity]: 'BCBS Blue Cross And Blue Shield Kansas City',
  [DeprecatedPayor.BeaconHealthOptions]: 'Beacon Health Options',
  [DeprecatedPayor.BindBenefitsInc]: 'Bind Benefits Inc',
  [DeprecatedPayor.BlueCross]: 'Blue Cross',
  [DeprecatedPayor.BlueCrossBlueShieldofArizona]: 'Blue Cross Blue Shield Of Arizona',
  [DeprecatedPayor.BlueCrossBlueShieldofMinnesota]: 'Blue Cross Blue Shield Of Minnesota',
  [DeprecatedPayor.BlueCrossBlueShieldofMontana]: 'Blue Cross Blue Shield Of Montana',
  [DeprecatedPayor.BlueCrossBlueShieldofNorthDakota]: 'Blue Cross Blue Shield Of North Dakota',
  [DeprecatedPayor.BlueCrossBlueShieldofNorthCarolina]: 'Blue Cross Blue Shield Of North Carolina',
  [DeprecatedPayor.BlueCrossBlueShieldWellmark]: 'Blue Cross Blue Shield Wellmark',
  [DeprecatedPayor.BlueCrossFEP]: 'Blue Cross FEP',
  [DeprecatedPayor.BlueCrossMediCal]: 'Blue Cross Medi-Cal',
  [DeprecatedPayor.BlueCrossMedicareAdvantage]: 'Blue Cross Medicare Advantage',
  [DeprecatedPayor.BlueCrossNoMedicare]: 'Blue Cross No Medicare',
  [DeprecatedPayor.BlueEssentials]: 'Blue essentials',
  [DeprecatedPayor.BlueShield]: 'Blue Shield',
  [DeprecatedPayor.BlueShieldMedicare]: 'Blue Shield Medicare',
  [DeprecatedPayor.BridgeSpanHealth]: 'Bridgespan Health',
  [DeprecatedPayor.CencalHealth]: 'Cencal Health',
  [DeprecatedPayor.Centene]: 'Ambetter / Amwell',
  [DeprecatedPayor.CentralCaliforniaAllianceForHealth]: 'Central California Alliance for Health',
  [DeprecatedPayor.ChampVa]: 'ChampVA',
  [DeprecatedPayor.Cigna]: 'CIGNA',
  [DeprecatedPayor.CignaApwu]: 'Cigna - APWU',
  [DeprecatedPayor.CignaHealthSpring]: 'Cigna HealthSpring',
  [DeprecatedPayor.CignaNalc]: 'Cigna - NALC',
  [DeprecatedPayor.Coastal]: 'Coastal',
  [DeprecatedPayor.CoastalAlt]: 'Coastal Alt',
  [DeprecatedPayor.ColonialPennLife]: 'Colonial Penn Life',
  [DeprecatedPayor.CommercialInsuranceOther]: 'Commercial Insurance - Other',
  [DeprecatedPayor.Coresource]: 'Coresource',
  [DeprecatedPayor.CoresourceOfFmh]: 'Coresource of FMH',
  [DeprecatedPayor.Coventry]: 'Coventry',
  [DeprecatedPayor.Covid19HRSAUninsured]: 'Covid 19 HRSA Uninsured',
  [DeprecatedPayor.DellChildrenChip]: "Dell Children's Chip",
  [DeprecatedPayor.DellChildrenMedicaid]: "Dell Children's Medicaid",
  [DeprecatedPayor.EMBS]: 'EMBS',
  [DeprecatedPayor.Emi]: 'EMI',
  [DeprecatedPayor.ExperienceHealth]: 'Experience Health',
  [DeprecatedPayor.FederatedMutualInsurance]: 'Federated Mutual Insurance',
  [DeprecatedPayor.Geha]: 'GEHA',
  [DeprecatedPayor.GoldenRule]: 'Golden Rule',
  [DeprecatedPayor.GreatAmerican]: 'Great American',
  [DeprecatedPayor.GreatWest]: 'Great West',
  [DeprecatedPayor.GreatWestCigna]: 'Great West-Cigna',
  [DeprecatedPayor.GroupAndPensionAdministrators]: 'Group and Pension Administrators',
  [DeprecatedPayor.GroupHealthCoopEauClaire]: 'Group Health Coop-Eau Claire',
  [DeprecatedPayor.HealtheAtCerner]: 'Healthe At Cerner',
  [DeprecatedPayor.HealthNet]: 'Healthnet',
  [DeprecatedPayor.HealthNetPrisoner]: 'Healthnet Prisoner',
  [DeprecatedPayor.HealthPartners]: 'Health Partners',
  [DeprecatedPayor.HealthyBlueNorthCarolina]: 'Healthy Blue North Carolina',
  [DeprecatedPayor.HMCAPS]: 'HMC APS',
  [DeprecatedPayor.HorizonBlueCrossBlueShield]: 'Horizon Blue Cross Blue Shield',
  [DeprecatedPayor.HorizonNJHealth]: 'Horizon NJ Health',
  [DeprecatedPayor.Humana]: 'Humana',
  [DeprecatedPayor.HumanaMedicareAdvantage]: 'Humana Medicare Advantage',
  [DeprecatedPayor.HumanaMedicareAdvantageSupplement]: 'Humana Medicare Supplement',
  [DeprecatedPayor.IlliniCareHealth]: 'IlliniCare Health',
  [DeprecatedPayor.IllinoisMedicaid]: 'Illinois Medicaid',
  [DeprecatedPayor.IndependenceBlueCross]: 'Independence Blue Cross',
  [DeprecatedPayor.IndependenceBlueCrossBlueShield]: 'Independence Blue Cross Blue Shield',
  [DeprecatedPayor.KaiserMedicare]: 'Kaiser Medicare',
  [DeprecatedPayor.LifeWise]: 'Lifewise',
  [DeprecatedPayor.LouisianaHealthcareConnections]: 'Louisiana Healthcare Connections',
  [DeprecatedPayor.LoyalAmerican]: 'Loyal American',
  [DeprecatedPayor.MagellanHealthServices]: 'Magellan Health Services',
  [DeprecatedPayor.MailHandlers]: 'Mail Handlers',
  [DeprecatedPayor.MaricopaCareAdvantage]: 'Maricopa Care Advantage',
  [DeprecatedPayor.Medica]: 'Medica',
  [DeprecatedPayor.Medicaid]: 'Medicaid',
  [DeprecatedPayor.MedicaidAmerigroup]: 'Medicaid - Amerigroup',
  [DeprecatedPayor.MedicaidBcbsStar]: 'Medicaid - BCBS Star',
  [DeprecatedPayor.MedicaidKansas]: 'Medicaid - Kansas',
  [DeprecatedPayor.MedicaidMissouri]: 'Medicaid Missouri',
  [DeprecatedPayor.MedicaidSuperiorHealthPlan]: 'Medicaid Superior',
  [DeprecatedPayor.MedicaidTmhp]: 'Medicaid - TMHP',
  [DeprecatedPayor.MediCal]: 'Medi-Cal',
  [DeprecatedPayor.Medicare]: 'Medicare',
  [DeprecatedPayor.MedicareReplacement]: 'Medicare Replacement',
  [DeprecatedPayor.Mediview]: 'Mediview',
  [DeprecatedPayor.MeridianHealthPlan]: 'Meridian Health Plan',
  [DeprecatedPayor.MeritainHealth]: 'Meritain Health',
  [DeprecatedPayor.MHN]: 'MHN',
  [DeprecatedPayor.MutualOfOmaha]: 'Mutual of Omaha',
  [DeprecatedPayor.NALC]: 'NALC',
  [DeprecatedPayor.Neighborhood]: 'Neighborhood',
  [DeprecatedPayor.SanfordHealthPlan]: 'Sanford Health Plan',
  [DeprecatedPayor.SanfordHealthPlanNorthDakotaPublicEmployeeRetirementSystem]:
    'Sanford Health Plan North Dakota Public Employee Retirement System',
  [DeprecatedPayor.OptumVA]: 'Optum VA',
  [DeprecatedPayor.Oscar]: 'Oscar',
  [DeprecatedPayor.Other]: 'Other',
  [DeprecatedPayor.OxfordHealth]: 'Oxford Health',
  [DeprecatedPayor.Pehp]: 'PEHP',
  [DeprecatedPayor.Phcs]: 'PHCS',
  [DeprecatedPayor.PhysiciansMutual]: 'Physicians Mutual',
  [DeprecatedPayor.PlannedAdministratorsInc]: 'Planned Administrators Inc',
  [DeprecatedPayor.PreferredOne]: 'PreferredOne',
  [DeprecatedPayor.PremeraBlueCrossWA]: 'Premera Blue Cross WA',
  [DeprecatedPayor.PremeraBlueCrossBlueShield]: 'Premera Blue Cross Blue Shield',
  [DeprecatedPayor.ProvidenceHealthPlans]: 'Providence Health Plans',
  [DeprecatedPayor.QuickTrip]: 'QuickTrip',
  [DeprecatedPayor.RegenceBlueCross]: 'Regence Blue Cross',
  [DeprecatedPayor.RegenceBlueShieldWA]: 'Regence Blue Shield WA',
  [DeprecatedPayor.RegenceBlueCrossBlueShield]: 'Regence Blue Cross Blue Shield',
  [DeprecatedPayor.SecurityAdministrativeServices]: 'Security Administrative Services',
  [DeprecatedPayor.SecurityHealthPlan]: 'Security Health Plan',
  [DeprecatedPayor.SelectHealth]: 'Select Health',
  [DeprecatedPayor.SenderoIdealcare]: 'Sendero Idealcare',
  [DeprecatedPayor.StandardLife]: 'Standard Life',
  [DeprecatedPayor.StateFarm]: 'State Farm',
  [DeprecatedPayor.SunflowerHealth]: 'Sunflower Health',
  [DeprecatedPayor.TransAmerica]: 'TransAmerica',
  [DeprecatedPayor.Tricare]: 'Tricare',
  [DeprecatedPayor.TricareWest]: 'Tricare West',
  [DeprecatedPayor.TricareForLife]: 'Tricare for Life',
  [DeprecatedPayor.Triwest]: 'Triwest',
  [DeprecatedPayor.UhcCommunityMedicaidEvercare]: 'UHC Community/Evercare',
  [DeprecatedPayor.UhcGoldenRule]: 'UHC Golden Rule',
  [DeprecatedPayor.UhcMedica]: 'UHC Medica',
  [DeprecatedPayor.Umr]: 'UMR',
  [DeprecatedPayor.UnionPacificRrInsuranceCo]: 'Union Pacific RR Insurance Co',
  [DeprecatedPayor.UnitedAmerican]: 'United American',
  [DeprecatedPayor.UnitedBehavioral]: 'United Behavioral',
  [DeprecatedPayor.UnitedHealthcare]: 'United Healthcare',
  [DeprecatedPayor.UnitedHealthcareCommunityPlanKansas]: 'United Healthcare Community Plan Kansas',
  [DeprecatedPayor.UnitedHealthcareMedicare]: 'United Healthcare Medicare',
  [DeprecatedPayor.UnitedHealthcareStudentResources]: 'United Healthcare Student Resources',
  [DeprecatedPayor.UsaaLifeInsuranceCo]: 'USAA Life Insurance Co',
  [DeprecatedPayor.UtahMedicaid]: 'Utah Medicaid',
  [DeprecatedPayor.VeteransAdministration]: 'Veterans Administration',
  [DeprecatedPayor.WebTpaInc]: 'WEB-TPA Inc',
  [DeprecatedPayor.WellcareMedicareAdvantage]: 'Wellcare Medicare Advantage',
  [DeprecatedPayor.WesternGrowers]: 'Western Growers',
  [DeprecatedPayor.WellCareByAllwell]: 'Wellcare By Allwell',
};
